import { useState, useRef } from 'react';
import { useSoundboard, SoundboardGroup } from '../hooks/useSoundboard';

interface SoundboardPanelProps {
  autoPlayEnabled?: boolean;
}

type DragItem =
  | { type: 'button'; groupId: string; buttonIndex: number }
  | { type: 'group'; groupIndex: number };

function SoundboardPanel({ autoPlayEnabled = true }: SoundboardPanelProps) {
  const {
    groups,
    activeButtonId,
    volume,
    loop,
    setLoop,
    isEditMode,
    setIsEditMode,
    play,
    setVolume,
    addGroup,
    removeGroup,
    renameGroup,
    reorderGroups,
    toggleGroupCollapse,
    addButton,
    removeButton,
    renameButton,
    reorderButtons,
    moveButton,
  } = useSoundboard({ autoPlayEnabled });

  const [newLabel, setNewLabel] = useState('');
  const [newGroupLabel, setNewGroupLabel] = useState('');
  const [targetGroupId, setTargetGroupId] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drag state
  const dragItemRef = useRef<DragItem | null>(null);
  const [dragOverGroupIndex, setDragOverGroupIndex] = useState<number | null>(null);
  const [dragOverButton, setDragOverButton] = useState<{ groupId: string; index: number } | null>(null);
  const [dragOverGroupHeader, setDragOverGroupHeader] = useState<string | null>(null);

  // Default target group to first group
  const effectiveTargetGroupId = targetGroupId || groups[0]?.id || '';

  const handleAddSound = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file || !newLabel.trim() || !effectiveTargetGroupId) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('audio', file);

      const res = await fetch('/api/upload/audio', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error('Upload failed');

      const data = await res.json();
      addButton(effectiveTargetGroupId, newLabel.trim(), data.url);
      setNewLabel('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('[Soundboard] Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddGroup = () => {
    if (!newGroupLabel.trim()) return;
    addGroup(newGroupLabel.trim());
    setNewGroupLabel('');
  };

  // --- Drag handlers ---

  const handleButtonDragStart = (groupId: string, buttonIndex: number, e: React.DragEvent) => {
    dragItemRef.current = { type: 'button', groupId, buttonIndex };
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleGroupDragStart = (groupIndex: number, e: React.DragEvent) => {
    dragItemRef.current = { type: 'group', groupIndex };
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleButtonDragOver = (groupId: string, index: number, e: React.DragEvent) => {
    const item = dragItemRef.current;
    if (!item || item.type !== 'button') return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverButton({ groupId, index });
    setDragOverGroupHeader(null);
  };

  const handleButtonDrop = (targetGroupId: string, targetIndex: number, e: React.DragEvent) => {
    e.preventDefault();
    const item = dragItemRef.current;
    if (!item || item.type !== 'button') return;

    if (item.groupId === targetGroupId) {
      // Riordino dentro lo stesso gruppo
      if (item.buttonIndex !== targetIndex) {
        reorderButtons(targetGroupId, item.buttonIndex, targetIndex);
      }
    } else {
      // Sposta in altro gruppo: prima muovi, il button finirà in fondo
      const group = groups.find((g) => g.id === item.groupId);
      const btn = group?.buttons[item.buttonIndex];
      if (btn) {
        moveButton(item.groupId, btn.id, targetGroupId);
      }
    }

    dragItemRef.current = null;
    setDragOverButton(null);
  };

  const handleGroupHeaderDragOver = (groupId: string, groupIndex: number, e: React.DragEvent) => {
    const item = dragItemRef.current;
    if (!item) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (item.type === 'group') {
      setDragOverGroupIndex(groupIndex);
      setDragOverGroupHeader(null);
    } else if (item.type === 'button') {
      // Button dragged onto group header → move to this group
      setDragOverGroupHeader(groupId);
      setDragOverButton(null);
    }
  };

  const handleGroupHeaderDrop = (targetGroupId: string, targetGroupIndex: number, e: React.DragEvent) => {
    e.preventDefault();
    const item = dragItemRef.current;
    if (!item) return;

    if (item.type === 'group') {
      if (item.groupIndex !== targetGroupIndex) {
        reorderGroups(item.groupIndex, targetGroupIndex);
      }
    } else if (item.type === 'button') {
      const group = groups.find((g) => g.id === item.groupId);
      const btn = group?.buttons[item.buttonIndex];
      if (btn && item.groupId !== targetGroupId) {
        moveButton(item.groupId, btn.id, targetGroupId);
      }
    }

    dragItemRef.current = null;
    setDragOverGroupIndex(null);
    setDragOverGroupHeader(null);
  };

  const handleDragEnd = () => {
    dragItemRef.current = null;
    setDragOverGroupIndex(null);
    setDragOverButton(null);
    setDragOverGroupHeader(null);
  };

  const renderButton = (group: SoundboardGroup, btn: typeof group.buttons[0], index: number) => (
    <div
      key={btn.id}
      draggable={isEditMode}
      onDragStart={(e) => handleButtonDragStart(group.id, index, e)}
      onDragOver={(e) => handleButtonDragOver(group.id, index, e)}
      onDrop={(e) => handleButtonDrop(group.id, index, e)}
      onDragEnd={handleDragEnd}
      className={`flex items-center gap-1 ${
        isEditMode && dragOverButton?.groupId === group.id && dragOverButton?.index === index
          ? 'border-t-2 border-purple-400'
          : ''
      }`}
    >
      {isEditMode && (
        <span className="text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0 text-xs select-none">&#x2630;</span>
      )}
      {isEditMode ? (
        <input
          type="text"
          defaultValue={btn.label}
          onBlur={(e) => {
            const val = e.target.value.trim();
            if (val && val !== btn.label) renameButton(group.id, btn.id, val);
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          className="flex-1 text-xs px-2 py-1.5 rounded bg-gray-800 border border-gray-600 text-white focus:outline-none focus:border-purple-500 min-w-0"
        />
      ) : (
        <button
          onClick={() => play(btn.id)}
          className={`flex-1 text-left text-xs px-2 py-1.5 rounded transition truncate ${
            activeButtonId === btn.id
              ? 'bg-purple-600 ring-2 ring-purple-400 text-white'
              : 'bg-gray-800 hover:bg-gray-700 text-gray-200'
          } cursor-pointer`}
        >
          {btn.label}
        </button>
      )}
      {isEditMode && (
        <button
          onClick={() => removeButton(group.id, btn.id)}
          className="text-red-400 hover:text-red-300 text-sm px-0.5 flex-shrink-0"
          title="Elimina"
        >
          &times;
        </button>
      )}
    </div>
  );

  const renderGroup = (group: SoundboardGroup, groupIndex: number) => {
    const isCollapsed = group.collapsed && !isEditMode;

    return (
      <div key={group.id} className="mb-2">
        {/* Group header */}
        <div
          draggable={isEditMode}
          onDragStart={(e) => handleGroupDragStart(groupIndex, e)}
          onDragOver={(e) => handleGroupHeaderDragOver(group.id, groupIndex, e)}
          onDrop={(e) => handleGroupHeaderDrop(group.id, groupIndex, e)}
          onDragEnd={handleDragEnd}
          className={`flex items-center gap-1.5 px-1 py-1 rounded cursor-pointer select-none ${
            isEditMode && dragOverGroupIndex === groupIndex
              ? 'border-t-2 border-purple-400'
              : ''
          } ${
            dragOverGroupHeader === group.id
              ? 'bg-purple-900/40 ring-1 ring-purple-400'
              : ''
          }`}
        >
          {isEditMode && (
            <span className="text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0 text-xs select-none">&#x2630;</span>
          )}

          {isEditMode ? (
            <input
              type="text"
              defaultValue={group.label}
              onClick={(e) => e.stopPropagation()}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (val && val !== group.label) renameGroup(group.id, val);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              className="flex-1 text-xs font-semibold px-2 py-1 rounded bg-gray-800 border border-gray-600 text-white focus:outline-none focus:border-purple-500 min-w-0"
            />
          ) : (
            <div
              onClick={() => toggleGroupCollapse(group.id)}
              className="flex-1 flex items-center gap-1.5 text-xs font-semibold text-gray-300 hover:text-white transition"
            >
              <span className="text-[10px] text-gray-500">{group.collapsed ? '\u25B8' : '\u25BE'}</span>
              <span>{group.label}</span>
              <span className="text-[10px] text-gray-600 font-normal">({group.buttons.length})</span>
            </div>
          )}

          {isEditMode && (
            <button
              onClick={() => removeGroup(group.id)}
              className="text-red-400 hover:text-red-300 text-sm px-0.5 flex-shrink-0"
              title="Elimina gruppo"
            >
              &times;
            </button>
          )}
        </div>

        {/* Group buttons */}
        {!isCollapsed && (
          <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 pl-2 pr-1 pt-1">
            {group.buttons.map((btn, index) => renderButton(group, btn, index))}
            {group.buttons.length === 0 && (
              <p className="col-span-2 text-[10px] text-gray-700 italic py-1">
                Nessun suono
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
        <h2 className="text-sm font-bold tracking-wide uppercase text-gray-300">Soundboard</h2>
        <button
          onClick={() => setIsEditMode(!isEditMode)}
          className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 transition text-gray-300"
        >
          {isEditMode ? 'Fatto' : 'Modifica'}
        </button>
      </div>

      {/* Volume slider */}
      <div className="px-3 py-2 border-b border-gray-800 flex items-center gap-2">
        <span className="text-xs text-gray-500">Vol</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          className="flex-1 h-1 accent-purple-500"
        />
      </div>

      {/* Loop toggle */}
      <div className="px-3 py-2 border-b border-gray-800 flex items-center gap-2">
        <button
          onClick={() => setLoop(!loop)}
          className={`px-3 py-1 rounded text-xs font-bold transition ${
            loop
              ? 'bg-purple-600 text-white'
              : 'bg-gray-700 text-gray-400'
          }`}
        >
          Loop
        </button>
      </div>

      {/* Groups */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {groups.length === 0 && (
          <p className="text-xs text-gray-600 text-center mt-4">
            {isEditMode ? 'Aggiungi un gruppo qui sotto' : 'Nessun suono configurato'}
          </p>
        )}
        {groups.map((group, index) => renderGroup(group, index))}
      </div>

      {/* Edit mode forms */}
      {isEditMode && (
        <div className="px-3 py-2 border-t border-gray-700 space-y-3">
          {/* Add group */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Nome gruppo"
              value={newGroupLabel}
              onChange={(e) => setNewGroupLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddGroup(); }}
              className="flex-1 text-sm px-2 py-1 rounded bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
            <button
              onClick={handleAddGroup}
              disabled={!newGroupLabel.trim()}
              className="text-xs px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition text-gray-300 whitespace-nowrap"
            >
              + Gruppo
            </button>
          </div>

          {/* Add sound */}
          {groups.length > 0 && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Nome suono"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  className="flex-1 text-sm px-2 py-1 rounded bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
                <select
                  value={effectiveTargetGroupId}
                  onChange={(e) => setTargetGroupId(e.target.value)}
                  className="text-xs px-2 py-1 rounded bg-gray-800 border border-gray-600 text-white focus:outline-none focus:border-purple-500 max-w-[120px]"
                >
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.label}</option>
                  ))}
                </select>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg"
                className="w-full text-xs text-gray-400 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-700 file:text-gray-300 file:cursor-pointer"
              />
              <button
                onClick={handleAddSound}
                disabled={isUploading || !newLabel.trim()}
                className="w-full text-sm py-1.5 rounded bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition font-medium"
              >
                {isUploading ? 'Caricamento...' : 'Aggiungi suono'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SoundboardPanel;
